import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation, useBeforeUnload } from 'react-router-dom';
import { useProcessing } from '../context/ProcessingContext';

export function useBlocker(shouldBlock: boolean = false) {
  const { isProcessing } = useProcessing();
  const navigate = useNavigate();
  const location = useLocation();

  // Block navigation attempts
  const blockNavigation = useCallback((to: string): boolean => {
    if (isProcessing || shouldBlock) {
      if (to === location.pathname) {
        return false; // Don't block navigation to the same page
      }
      const message = isProcessing 
        ? 'A process is currently running. Are you sure you want to leave? This will cancel the current operation.'
        : 'Are you sure you want to leave? Your changes will be lost.';
      
      const shouldLeave = window.confirm(message);
      return !shouldLeave;
    }
    return false;
  }, [isProcessing, shouldBlock, location.pathname]);

  // Handle page refresh and tab close
  useBeforeUnload(
    useCallback((event) => {
      if (isProcessing || shouldBlock) {
        const message = isProcessing 
          ? 'A process is currently running. Are you sure you want to leave? This will cancel the current operation.'
          : 'Are you sure you want to leave? Your changes will be lost.';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    }, [isProcessing, shouldBlock])
  );

  // Handle browser navigation
  useEffect(() => {
    if (!isProcessing && !shouldBlock) return;

    const unblock = navigate((to) => {
      if (to === location.pathname) {
        return; // Don't block navigation to the same page
      }
      const message = isProcessing 
        ? 'A process is currently running. Are you sure you want to leave? This will cancel the current operation.'
        : 'Are you sure you want to leave? Your changes will be lost.';
      
      if (!window.confirm(message)) {
        return false;
      }
    });

    return unblock;
  }, [isProcessing, shouldBlock, navigate, location.pathname]);

  return { blockNavigation };
}